// Client-side mirror of backend/desking.py compute() — same conventions so the
// worksheet recalculates on every keystroke and still matches what the server saves.
export const PERIODS = { weekly: 52, biweekly: 26, semimonthly: 24, monthly: 12 };
export const FREQ_LABEL = { weekly: "Weekly", biweekly: "Bi-weekly", semimonthly: "Semi-monthly", monthly: "Monthly" };
export const FREQUENCIES = Object.keys(PERIODS);

const n2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
export const r2 = n2;

export function paymentSchedule(principal, apr, termMonths, frequency, roundPayment) {
  const perYear = PERIODS[frequency] || 26;
  const n = Math.round(((Number(termMonths) || 0) / 12) * perYear);
  if (n <= 0 || principal <= 0) {
    return { n_payments: Math.max(n, 0), payment: 0, final_payment: 0, total_of_payments: 0, cost_of_borrowing: 0, periodic_rate: 0, per_year: perYear };
  }
  const i = (Number(apr) || 0) / 100 / perYear;
  const raw = i === 0 ? principal / n : (principal * i) / (1 - Math.pow(1 + i, -n));
  const pmt = roundPayment ? Math.ceil(raw) : n2(raw);
  let bal = principal;
  for (let k = 0; k < n - 1; k++) {
    bal = bal * (1 + i) - pmt;
    if (bal < 0) bal = 0;
  }
  const final = n2(bal * (1 + i));
  const total = n2(pmt * (n - 1) + final);
  return {
    n_payments: n,
    payment: n2(pmt),
    final_payment: final,
    total_of_payments: total,
    cost_of_borrowing: n2(total - principal),
    periodic_rate: Math.round(i * 1e8) / 1e6,
    per_year: perYear,
  };
}

export function luxuryTaxFor(price, cfg) {
  if (!cfg?.enabled || price <= (cfg.threshold ?? 100000)) return 0;
  const over = (price - (cfg.threshold ?? 100000)) * ((cfg.rate_over ?? 20) / 100);
  const full = price * ((cfg.rate_full ?? 10) / 100);
  return n2(Math.min(over, full));
}

export function computeWorksheet(ws, settings) {
  const rate = (Number(ws.tax_rate) || 0) / 100;
  const exempt = Boolean(ws.tax_exempt);
  const priceGross = n2(ws.selling_price);
  const discount = n2(ws.discount);
  const rebBefore = n2(ws.rebate_before_tax);
  const rebAfter = n2(ws.rebate_after_tax);
  const price = n2(Math.max(priceGross - discount - rebBefore, 0));

  const fees = (ws.fees || []).filter((f) => f.enabled !== false);
  const feesT = n2(fees.filter((f) => f.taxable !== false).reduce((s, f) => s + (Number(f.amount) || 0), 0));
  const feesN = n2(fees.filter((f) => f.taxable === false).reduce((s, f) => s + (Number(f.amount) || 0), 0));

  const prods = (ws.products || []).filter((p) => p.enabled !== false);
  const prodT = n2(prods.filter((p) => p.taxable !== false).reduce((s, p) => s + (Number(p.price) || 0), 0));
  const prodN = n2(prods.filter((p) => p.taxable === false).reduce((s, p) => s + (Number(p.price) || 0), 0));
  const prodCost = n2(prods.reduce((s, p) => s + (Number(p.cost) || 0), 0));
  const prodTotal = n2(prodT + prodN);

  const luxCfg = settings?.luxury_tax || {};
  let lux = 0;
  if (ws.luxury_tax_override !== null && ws.luxury_tax_override !== undefined && ws.luxury_tax_override !== "") lux = n2(ws.luxury_tax_override);
  else if (ws.luxury_tax_enabled !== false) lux = luxuryTaxFor(price, luxCfg);
  const luxTaxable = luxCfg.taxable !== false;

  const trade = ws.trade || {};
  const hasTrade = Boolean(ws.has_trade);
  const allowance = hasTrade ? n2(trade.allowance) : 0;
  const acv = hasTrade ? n2(trade.acv) : 0;
  const lien = hasTrade ? n2(trade.lien_amount) : 0;
  const tradeCredit = hasTrade ? Math.min(allowance, price + feesT + prodT) : 0;

  const taxableBase = n2(Math.max(price - tradeCredit, 0) + feesT + prodT + (luxTaxable ? lux : 0));
  const tax = exempt ? 0 : n2(taxableBase * rate);
  const taxSavings = exempt || !hasTrade ? 0 : n2(tradeCredit * rate);

  const otd = n2(price + feesT + feesN + prodTotal + lux + tax - rebAfter);
  const fin = ws.finance || {};
  const cashDown = n2(fin.cash_down);
  const deposit = n2(fin.deposit);
  const upfront = n2(prods.filter((p) => p.financed === false)
    .reduce((s, p) => s + (Number(p.price) || 0) + (p.taxable !== false && !exempt ? (Number(p.price) || 0) * rate : 0), 0));

  const balance = n2(otd - allowance + lien - deposit - cashDown);
  const financed = n2(Math.max(balance - upfront, 0));
  const cashBack = n2(Math.max(-(balance - upfront), 0));

  const deferDays = Number(fin.deferral_days) || 0;
  const apr = Number(fin.apr) || 0;
  const deferInterest = deferDays > 0 ? n2(financed * (apr / 100) * (deferDays / 365)) : 0;
  const principal = n2(financed + deferInterest);
  const sched = paymentSchedule(principal, apr, fin.term_months, fin.frequency || "biweekly", Boolean(fin.round_payment));

  const costBasis = n2(ws.cost_basis);
  const pack = n2(ws.pack);
  const overAllowance = hasTrade ? n2(Math.max(allowance - acv, 0)) : 0;
  const frontGross = n2(price - costBasis - pack - overAllowance);
  const reserve = n2(fin.finance_reserve);
  const backGross = n2(prodTotal - prodCost + reserve);

  return {
    price_gross: priceGross, discount, rebate_before_tax: rebBefore, rebate_after_tax: rebAfter,
    selling_price_net: price,
    fees_taxable: feesT, fees_non_taxable: feesN, fees_total: n2(feesT + feesN),
    fee_income: n2(fees.filter((f) => f.id !== "licensing").reduce((s, f) => s + (Number(f.amount) || 0), 0)),
    products_taxable: prodT, products_non_taxable: prodN, products_total: prodTotal,
    products_cost: prodCost, products_upfront: upfront,
    luxury_tax: lux,
    trade_allowance: allowance, trade_acv: acv, trade_lien: lien, trade_credit: tradeCredit,
    net_trade_equity: n2(allowance - lien), negative_equity: n2(Math.max(lien - allowance, 0)),
    over_allowance: overAllowance,
    taxable_base: taxableBase, tax_rate: n2(ws.tax_rate), tax, tax_exempt: exempt,
    tax_savings_from_trade: taxSavings,
    out_the_door: otd, cash_down: cashDown, deposit, balance,
    amount_financed: financed, cash_back: cashBack, cash_due: n2(Math.max(balance, 0)),
    deferral_days: deferDays, deferral_interest: deferInterest, financed_principal: principal,
    due_at_delivery: n2(cashDown + upfront),
    mode: fin.mode || "finance", frequency: fin.frequency || "biweekly",
    frequency_label: FREQ_LABEL[fin.frequency || "biweekly"], apr, term_months: Number(fin.term_months) || 0,
    ...sched,
    cost_basis: costBasis, pack, front_gross: frontGross, back_gross: backGross,
    finance_reserve: reserve, total_gross: n2(frontGross + backGross),
  };
}

/** Payment for a hypothetical variation of the worksheet (grid cells, solves, menu). */
export const paymentFor = (ws, settings, patch) => computeWorksheet(deepPatch(ws, patch), settings).payment;

export function deepPatch(ws, patch = {}) {
  const out = { ...ws };
  for (const [k, v] of Object.entries(patch)) {
    out[k] = v && typeof v === "object" && !Array.isArray(v) ? { ...(ws[k] || {}), ...v } : v;
  }
  return out;
}

/**
 * Bisection solve: find the value of one variable that hits a target payment.
 * `apply(value)` returns the patch for that candidate value.
 */
export function solveFor({ ws, settings, target, apply, lo, hi, decreasing = true, tolerance = 0.01, iterations = 60 }) {
  let a = lo, b = hi;
  const pay = (v) => paymentFor(ws, settings, apply(v));
  for (let k = 0; k < iterations; k++) {
    const mid = (a + b) / 2;
    const p = pay(mid);
    if (Math.abs(p - target) <= tolerance) return mid;
    const tooHigh = p > target;
    if (decreasing ? tooHigh : !tooHigh) a = mid;
    else b = mid;
  }
  return (a + b) / 2;
}

export const money = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
export const money0 = (n) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(Number(n) || 0);

export const STATUS_TONE = { worksheet: "neutral", presented: "blue", accepted: "green", converted: "gold" };
